import { useState, useMemo, useEffect, type ReactNode } from 'react'
import {
  useProducts,
  useProductTemplates,
  useProductVariants,
  useProductFormMetadata,
  useCreateProduct,
  useProductCategories,
  useProductFilterFacets,
} from '@/admin/hooks/use-products'
import {
  ProductFilterMenu,
  type ProductFilterValue,
} from '@/shared/components/ui/product-filter-menu'
import { useTableState } from '@/admin/hooks/use-table-state'
import { DataTable, type DataTableColumn, type ActiveFilter } from '@/admin/components/data-table'
import { useTableViews, useCreateTableView, useDeleteTableView, useUpdateTableView, useSetOrgDefaultView } from '@/admin/hooks/use-table-views'
import type { TableViewConfig } from '@/admin/types/table-views'
import { PageInfo } from '@/admin/components/page-info'
import { ListPageToolbar } from '@/admin/components/list-page-toolbar'
import { useTableExport } from '@/admin/hooks/use-table-export'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/shared/components/ui/select'
import { ImageUpload } from '@/admin/components/image-upload'
import { Image } from '@/shared/components/ui/image'
import { useNavigate } from 'react-router'
import { ChevronRight, ChevronDown, Plus, ArrowLeft, Package, List, Grid3x3, FolderTree } from 'lucide-react'
import { useOrg } from '@/admin/hooks/use-org'
import { LibraryCategoriesView } from './library-categories-view'
import { EmptyState } from '@/admin/components/shared/empty-state'
import { toast } from 'sonner'
import { toastApiError } from '@/shared/lib/api-error'
import { useTranslation } from 'react-i18next'
import type { ProductStatus, Product, ProductAttribute, FieldType } from '@/admin/types/products'
import { Label } from '@/shared/components/ui/label'
import { ProductGridCard } from '@/shared/components/ui/product-grid-card'
import { MetadataField, normaliseRegistrationField, isFieldRequired, formatDisplayValue, defineField, type SubmissionMode } from '@/shared/lib/forms'
import { cn } from '@/shared/lib/utils'
import { useFormatters } from '@/shared/lib/formatting'

type AttributeMeta = Pick<
  ProductAttribute,
  'display_name' | 'field_type' | 'configuration_data'
>

interface AttributeFormatOptions {
  currency?: string
  locale?: string
}

// Placeholder for empty cells.
const EMPTY_PLACEHOLDER = '-'

/**
 * Render a dynamic attribute column value (``attr:<code_name>`` columns)
 * for a product or variant row. Uses the attribute's field metadata to
 * route the raw ``product_data`` value through the shared
 * ``formatDisplayValue`` helper, so MONEY dicts render as currency,
 * SELECTs resolve to labels, etc. Falls back to a placeholder when empty.
 */
function renderAttributeValue(
  codeName: string,
  value: unknown,
  meta: AttributeMeta | undefined,
  options?: AttributeFormatOptions,
): ReactNode {
  if (value == null || value === '') return EMPTY_PLACEHOLDER
  // Fall back to a TEXT field when metadata is missing so formatDisplayValue's
  // smart fallbacks still render MONEY dicts as currency rather than [object Object].
  const field = defineField({
    code_name: codeName,
    display_name: meta?.display_name ?? codeName,
    field_type: (meta?.field_type ?? 'TEXT') as FieldType,
    configuration_data: meta?.configuration_data,
  })
  return formatDisplayValue(field, value, options)
}

function StatusBadge({ status }: { status: ProductStatus }) {
  const styles: Record<ProductStatus, string> = {
    SAVING: 'bg-[var(--bg-subtle)] text-[var(--ink-3)]',
    DRAFT: 'bg-[var(--bg-subtle)] text-[var(--ink-3)]',
    PUBLISHED: 'bg-[var(--status-ok-bg)] text-[var(--status-ok)]',
    DISCONTINUED: 'bg-[var(--bg-subtle)] text-[var(--ink-3)]',
  }
  const s = styles[status] ?? styles.DRAFT
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.08em]",
        s
      )}
    >
      {status.toLowerCase()}
    </span>
  );
}

function VariantRows({ productUuid, parentName, visibleColumns, attributeMeta, formatOptions, search }: { productUuid: string; parentName?: string; visibleColumns: DataTableColumn<Product>[]; attributeMeta: Map<string, AttributeMeta>; formatOptions: AttributeFormatOptions; search?: string }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { formatDate } = useFormatters()
  // When a library search is active, scope the expanded variants to the ones
  // that match (WP1-75 #1) so the parent stays visible but only its matching
  // variants show - not all of them.
  const { data: variants, isLoading } = useProductVariants(productUuid, search)

  function stripParentPrefix(name: string | undefined | null): string {
    if (!name) return ''
    if (!parentName) return name
    const prefix = `${parentName} - `
    return name.startsWith(prefix) ? name.slice(prefix.length) : name
  }

  if (isLoading) {
    return (
      <tr>
        <td colSpan={visibleColumns.length} className="px-4 py-1.5 text-[11px] text-[var(--ink-3)]">
          {t('products.loadingVariants')}
        </td>
      </tr>
    );
  }

  if (!variants?.length) {
    return (
      <tr>
        <td colSpan={visibleColumns.length} className="py-1.5 pl-[92px] pr-4 text-[11px] text-[var(--ink-3)]">
          {t('products.noVariants')}
        </td>
      </tr>
    );
  }

  function renderVariantCell(col: DataTableColumn<Product>, v: Product) {
    switch (col.id) {
      case 'name': {
        const sku = typeof v.product_data?.sku === 'string' || typeof v.product_data?.sku === 'number'
          ? String(v.product_data.sku)
          : ''
        return (
          <td key={col.id} className="py-1.5 pl-[92px] pr-4">
            <span className="inline-flex items-baseline gap-2">
              <span className="text-[12px] text-[var(--ink-2)]">
                {stripParentPrefix(v.cached_short_name) || t('products.untitledVariant')}
              </span>
              {sku && (
                <span className="text-[9px] text-[var(--green-border)]">
                  (SKU: {sku})
                </span>
              )}
            </span>
          </td>
        );
      }
      case 'status':
        return (
          <td key={col.id} className="px-4 py-1.5">
            <StatusBadge status={v.status} />
          </td>
        )
      case 'type':
        return (
          <td key={col.id} className="px-4 py-1.5 text-[11px] text-[var(--ink-3)]">
            {t('products.typeVariant')}
          </td>
        );
      case 'variants':
        return (
          <td key={col.id} className="px-4 py-1.5 text-[11px] text-[var(--ink-3)]">N/A</td>
        );
      case 'registrations':
        return (
          <td key={col.id} className="px-4 py-1.5 text-[11px] text-[var(--ink-3)]">
            {v.registration_count ?? 0}
          </td>
        );
      case 'created_at':
        return (
          <td key={col.id} className="px-4 py-1.5 text-[11px] text-[var(--ink-3)]">
            {formatDate(v.created_at)}
          </td>
        );
      default: {
        // Dynamic attribute column - render the variant's product_data value
        // (strip the ``attr:`` prefix) formatted via the shared helper so
        // MONEY columns (e.g. price_with_receipt / price_without_receipt)
        // render as currency.
        if (col.id.startsWith('attr:')) {
          const attrKey = col.id.slice(5)
          return (
            <td key={col.id} className="px-4 py-1.5 text-[11px] text-[var(--ink-2)]">
              {renderAttributeValue(attrKey, v.product_data?.[attrKey], attributeMeta.get(attrKey), formatOptions)}
            </td>
          );
        }
        return (
          <td key={col.id} className="px-4 py-1.5 text-[11px] text-[var(--ink-3)]">
            {v.product_template?.name ?? ''}
          </td>
        );
      }
    }
  }

  return (
    <>
      {variants.map((v) => (
        <tr
          key={v.uuid}
          onClick={() => navigate(`/manage/products/${v.uuid}`)}
          className="cursor-pointer border-b-[0.5px] border-[var(--rule)] transition-all duration-100 hover:bg-[var(--bg-subtle)]"
        >
          {visibleColumns.map((col) => renderVariantCell(col, v))}
        </tr>
      ))}
    </>
  )
}

export function ProductLibraryPage() {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([])
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set())
  const [showNewProduct, setShowNewProduct] = useState(false)
  const [activeViewUuid, setActiveViewUuid] = useState<string | null>(null)
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({})
  const [columnOrder, setColumnOrder] = useState<string[]>([])
  // Default view comes from a tenant setting (config, not tenant-specific
  // code): tenants with deep flat catalogues set
  // ``products_library_default_view: "categories"`` to open grouped;
  // everyone else keeps the flat list. We *derive* the active view from
  // the user's explicit choice (if any) falling back to the configured
  // default - so the default applies reactively once settings load,
  // without a setState-in-effect.
  const { tenantSettings, preferences } = useOrg()
  // Currency/locale fall-backs for formatting MONEY attribute columns. The
  // shared money formatter already reads the per-value currency embedded in
  // each ``{ amount, currency }`` dict; these are the fall-backs used when a
  // value omits its own currency.
  const formatOptions = useMemo(
    () => ({
      currency: preferences?.default_currency,
      locale: preferences?.default_language,
    }),
    [preferences?.default_currency, preferences?.default_language],
  )
  const defaultViewMode: 'list' | 'grid' | 'categories' = (() => {
    const def = (tenantSettings as Record<string, unknown> | undefined)?.[
      'products_library_default_view'
    ]
    return def === 'categories' || def === 'grid' || def === 'list'
      ? def
      : 'list'
  })()
  const [userViewMode, setUserViewMode] = useState<
    'list' | 'grid' | 'categories' | null
  >(null)
  const viewMode = userViewMode ?? defaultViewMode
  const setViewMode = setUserViewMode
  const { data: categories } = useProductCategories({ withProductCount: true })

  const { page, pageSize, sorting, setPage, setPageSize, setSorting, queryParams } =
    useTableState('products')

  // Views
  const { data: views = [] } = useTableViews('products')
  const createView = useCreateTableView()
  const deleteView = useDeleteTableView()
  const updateView = useUpdateTableView()
  const setOrgDefault = useSetOrgDefaultView()

  // Unified filter fly-out (shared with the consumer register page): the
  // library's single filtering surface, covering template / category / status
  // / type plus every product- and variant-level attribute, all tenant-driven.
  const { data: filterFacetData } = useProductFilterFacets()
  const filterFacets = useMemo(
    () => filterFacetData?.facets ?? [],
    [filterFacetData],
  )
  const [filterValues, setFilterValues] = useState<ProductFilterValue>({})

  // Selecting a variant-level facet reveals the matching variant rows; a
  // structural or product-level facet just narrows the base products.
  const variantFacetKeys = useMemo(
    () =>
      new Set(filterFacets.filter((f) => f.kind === 'variant').map((f) => f.key)),
    [filterFacets],
  )

  const params = useMemo(() => {
    const p = { ...queryParams }
    if (search) p.search = search
    activeFilters.forEach((f) => { p[f.field] = f.value })
    // Fly-out selections: structural keys (template_uuid / category_uuid /
    // status / product_type) and attr__<code>, each comma-joined so the
    // backend ORs multiple picks.
    let revealVariants = false
    for (const [key, values] of Object.entries(filterValues)) {
      if (!values.length) continue
      p[key] = values.join(',')
      if (variantFacetKeys.has(key)) revealVariants = true
    }
    if (revealVariants) p.exclude_variants = 'false'
    return p
  }, [queryParams, search, activeFilters, filterValues, variantFacetKeys])

  // The folder view fetches per category, so it needs the same filter state
  // the main query uses - the toolbar filters AND the fly-out selections. It
  // only ever received the former, so every fly-out filter was ignored there.
  const folderFilterParams = useMemo(() => {
    const p: Record<string, string> = {}
    activeFilters.forEach((f) => {
      p[f.field] = String(f.value)
    })
    for (const [key, values] of Object.entries(filterValues)) {
      if (values.length) p[key] = values.join(',')
    }
    return p
  }, [activeFilters, filterValues])

  const { data: productsData, isLoading, isFetching, isError, refetch } = useProducts(params)
  const { data: templates } = useProductTemplates()

  const products = useMemo(() => productsData?.results ?? [], [productsData])
  const totalCount = productsData?.count ?? 0

  // During a search, auto-expand the surfaced parents so their matching
  // variants (VariantRows filters to the search term) are visible without a
  // click. Left alone when not searching, so normal browsing is unaffected.
  useEffect(() => {
    if (!search.trim()) return
    setExpandedProducts((prev) => {
      const nextUuids = products
        .filter((p) => p.product_type === 'VARIANT_BASE')
        .map((p) => p.uuid)
      // Skip a new Set (and the re-render) when the parent set is unchanged -
      // search changes on every keystroke, so most updates are no-ops.
      if (prev.size === nextUuids.length && nextUuids.every((id) => prev.has(id))) {
        return prev
      }
      return new Set(nextUuids)
    })
  }, [search, products])

  function toggleExpand(uuid: string) {
    setExpandedProducts((prev) => {
      const next = new Set(prev)
      if (next.has(uuid)) next.delete(uuid)
      else next.add(uuid)
      return next
    })
  }

  // Map of attribute code_name -> field metadata, used by the row/variant
  // cell renderers to format dynamic attribute values (e.g. MONEY dicts)
  // through the shared ``formatDisplayValue`` helper. Built from every
  // template's attributes so it covers PRODUCT and PRODUCT_VARIANT levels.
  const attributeMetaByCode = useMemo(() => {
    const map = new Map<
      string,
      Pick<ProductAttribute, 'display_name' | 'field_type' | 'configuration_data'>
    >()
    for (const tmpl of templates ?? []) {
      for (const attr of tmpl.attributes ?? []) {
        if (!map.has(attr.code_name)) {
          map.set(attr.code_name, {
            display_name: attr.display_name,
            field_type: attr.field_type,
            configuration_data: attr.configuration_data,
          })
        }
      }
    }
    return map
  }, [templates])

  // Build dynamic attribute columns from the templates' attributes.
  // PRODUCT and PRODUCT_VARIANT fields both become columns (same as
  // adding a field on the template). Hidden by default; the user opts
  // in via the column toggle. Variant MONEY stays default-visible so
  // pricing still shows on variant rows without a toggle.
  const attributeColumns: DataTableColumn<Product>[] = useMemo(() => {
    if (!templates?.length) return []
    // Collect unique attributes across all templates (by code_name)
    const seen = new Set<string>()
    const cols: DataTableColumn<Product>[] = []
    for (const tmpl of templates) {
      for (const attr of tmpl.attributes ?? []) {
        const isProductLevel = attr.attribute_level === 'PRODUCT'
        const isVariantLevel = attr.attribute_level === 'PRODUCT_VARIANT'
        if (!isProductLevel && !isVariantLevel) continue
        if (seen.has(attr.code_name)) continue
        seen.add(attr.code_name)
        cols.push({
          id: `attr:${attr.code_name}`,
          header: attr.display_name,
          defaultVisible:
            isVariantLevel && attr.field_type === 'MONEY',
          cell: () => null, // rendered by ProductRow / VariantRows
          meta: attr.field_type === 'SELECT' || attr.field_type === 'MULTI_SELECT'
            ? { filterType: 'select' as const, filterField: `product_data__${attr.code_name}`, filterOptions: (attr.configuration_data?.options as Array<{ value: string; label: string }>) ?? [] }
            : { filterField: `product_data__${attr.code_name}` },
        })
      }
    }
    return cols
  }, [templates])

  // Build template filter options
  const templateFilterOptions = useMemo(
    () => (templates ?? []).map((tmpl) => ({ value: tmpl.uuid, label: tmpl.name })),
    [templates],
  )

  // Build category filter options (flatten tree so leaf categories are resolved)
  const categoryFilterOptions = useMemo(() => {
    const options: { value: string; label: string }[] = []
    function walk(cats: typeof categories, prefix = '') {
      for (const cat of cats ?? []) {
        const label = prefix ? `${prefix} > ${cat.name}` : cat.name
        options.push({ value: cat.uuid, label })
        if (cat.children?.length) walk(cat.children, label)
      }
    }
    walk(categories)
    return options
  }, [categories])

  const columns: DataTableColumn<Product>[] = useMemo(
    () => [
      {
        id: 'name',
        header: t('common.name'),
        sortable: true,
        sortField: 'cached_short_name',
        cell: () => null,
        meta: { filterType: 'text' as const, filterField: 'search' },
      },
      {
        id: 'template',
        header: t('products.columnTemplate'),
        sortable: true,
        sortField: 'product_template__name',
        cell: () => null,
        meta: {
          filterType: 'select' as const,
          filterField: 'template_uuid',
          filterOptions: templateFilterOptions,
        },
      },
      {
        id: 'category',
        header: t('products.columnCategory', { defaultValue: 'Category' }),
        sortable: true,
        sortField: 'category__name',
        defaultVisible: false,
        cell: () => null,
        meta: {
          filterType: 'select' as const,
          filterField: 'category_uuid',
          filterOptions: categoryFilterOptions,
        },
      },
      {
        id: 'status',
        header: t('products.columnStatus'),
        sortable: true,
        sortField: 'status',
        cell: () => null,
        meta: {
          filterType: 'select' as const,
          filterField: 'status',
          filterOptions: [
            { value: 'DRAFT', label: 'Draft' },
            { value: 'PUBLISHED', label: 'Published' },
            { value: 'DISCONTINUED', label: 'Discontinued' },
          ],
        },
      },
      {
        id: 'type',
        header: t('products.columnType'),
        sortable: true,
        sortField: 'product_type',
        cell: () => null,
        meta: {
          filterType: 'select' as const,
          filterField: 'product_type',
          filterOptions: [
            { value: 'PRODUCT', label: 'Product' },
            { value: 'VARIANT_BASE', label: 'Variant Base' },
          ],
        },
      },
      {
        id: 'variants',
        header: t('products.columnVariants'),
        cell: () => null,
      },
      {
        id: 'registrations',
        header: t('products.columnRegistrations', { defaultValue: 'Registrations' }),
        sortable: true,
        sortField: 'registration_count',
        cell: () => null,
      },
      ...attributeColumns,
      {
        id: 'created_at',
        header: t('products.columnCreated'),
        sortable: true,
        sortField: 'created_at',
        cell: () => null,
      },
    ],
    [t, attributeColumns, templateFilterOptions, categoryFilterOptions],
  )

  /** Build a complete config snapshot of the current visible columns + sorting + filters */
  function buildCurrentConfig(): TableViewConfig {
    const visibleIds = columns
      .filter((col) => {
        if (col.id in columnVisibility) return columnVisibility[col.id] !== false
        return col.defaultVisible !== false
      })
      .map((col) => col.id)
    return {
      columns: visibleIds,
      sorting: sorting ?? null,
      filters: activeFilters.map((f) => ({ field: f.field, operator: f.operator, value: f.value })),
    }
  }

  const { exportCsv, isExporting } = useTableExport({
    endpoint: '/products/items/',
    columns,
    columnVisibility,
    params,
  })

  return (
    <>
      {/* Header */}
      <div data-tour="product-library" className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between mb-2 gap-3 sm:gap-4">
        <h1 className="clara-title whitespace-nowrap">{t('products.title')}</h1>
        <button
          data-testid="product-library-add-btn"
          onClick={() => setShowNewProduct(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-[4px] text-[11px] uppercase tracking-[0.1em] transition-all duration-150 cursor-pointer whitespace-nowrap shrink-0 bg-[var(--green)] hover:bg-[var(--green-hover)] text-[var(--bg)]"
        >
          <Plus className="h-3.5 w-3.5" />
          {t('products.newProduct')}
        </button>
      </div>
      <div className="flex items-center gap-2 mb-6">
        <p className="whitespace-nowrap text-[13px] text-[var(--ink-3)]">
          {totalCount} {t('products.count', { count: totalCount })}
        </p>
        <PageInfo>
          {t('products.libraryInfo')}
        </PageInfo>
      </div>
      <ListPageToolbar<Product>
        tableId="products"
        search={search}
        onSearchChange={(v) => { setSearch(v); setPage(1) }}
        searchPlaceholder={t('products.searchPlaceholder')}
        views={views}
        activeViewUuid={activeViewUuid}
        onSelectView={(view) => {
          setActiveViewUuid(view?.uuid ?? null)
          if (view?.config) {
            if (view.config.columns?.length) {
              const savedCols = new Set(view.config.columns)
              const vis: Record<string, boolean> = {}
              columns.forEach((c) => { vis[c.id] = savedCols.has(c.id) })
              setColumnVisibility(vis)
            } else {
              setColumnVisibility({})
            }
            if (view.config.sorting) setSorting(view.config.sorting)
            else setSorting(null)
            if (view.config.filters?.length) {
              setActiveFilters(view.config.filters.map((f) => {
                const col = columns.find((c) => c.meta?.filterField === f.field)
                const displayValue = col?.meta?.filterOptions?.find((o) => o.value === f.value)?.label ?? f.value
                return { ...f, label: `${col?.header ?? f.field}: ${displayValue}` }
              }))
            } else {
              setActiveFilters([])
            }
          } else {
            setColumnVisibility({})
            setSorting(null)
            setActiveFilters([])
          }
        }}
        onSaveView={(name) =>
          createView.mutate(
            { table_id: 'products', name, config: buildCurrentConfig() },
            { onSuccess: (newView) => setActiveViewUuid(newView.uuid) },
          )
        }
        onDeleteView={(uuid) => { deleteView.mutate({ uuid, tableId: 'products' }); if (activeViewUuid === uuid) setActiveViewUuid(null) }}
        onUpdateView={(uuid) => updateView.mutate({ uuid, tableId: 'products', config: buildCurrentConfig() })}
        onRenameView={(uuid, name) => updateView.mutate({ uuid, tableId: 'products', name })}
        onSetOrgDefault={() => setOrgDefault.mutate({ table_id: 'products', name: 'Default', config: buildCurrentConfig() })}
        currentConfig={buildCurrentConfig()}
        columns={columns}
        activeFilters={activeFilters}
        onFiltersChange={(filters) => { setActiveFilters(filters); setPage(1) }}
        showColumnFilter={false}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={setColumnVisibility}
        columnOrder={columnOrder}
        onColumnOrderChange={setColumnOrder}
        onExport={exportCsv}
        isExporting={isExporting}
        trailingControls={
          <div className="flex items-center gap-2">
            <ProductFilterMenu
              facets={filterFacets}
              value={filterValues}
              onChange={(v) => { setFilterValues(v); setPage(1) }}
              triggerClassName="h-auto gap-1.5 rounded-[4px] px-2.5 py-1 text-[length:var(--cv-data-table-compact-size)] uppercase tracking-[0.1em]"
            />
            <div className="flex items-center overflow-hidden rounded-[4px] border-[0.5px] border-[var(--rule)]">
            <button
              onClick={() => { setViewMode('categories') }}
              title={t('products.viewByCategory', { defaultValue: 'Group by category' })}
              className={cn(
                'h-7 w-7 cursor-pointer transition-colors duration-100 flex items-center justify-center',
                viewMode === 'categories'
                  ? 'bg-[var(--bg-subtle)] text-[var(--ink)]'
                  : 'bg-transparent text-[var(--ink-3)]'
              )}
            >
              <FolderTree className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => { setViewMode('list') }}
              className={cn(
                'h-7 w-7 cursor-pointer border-l-[0.5px] border-[var(--rule)] transition-colors duration-100 flex items-center justify-center',
                viewMode === 'list'
                  ? 'bg-[var(--bg-subtle)] text-[var(--ink)]'
                  : 'bg-transparent text-[var(--ink-3)]'
              )}
            >
              <List className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => { setViewMode('grid') }}
              className={cn(
                'h-7 w-7 cursor-pointer border-l-[0.5px] border-[var(--rule)] transition-colors duration-100 flex items-center justify-center',
                viewMode === 'grid'
                  ? 'bg-[var(--bg-subtle)] text-[var(--ink)]'
                  : 'bg-transparent text-[var(--ink-3)]'
              )}
            >
              <Grid3x3 className="h-3.5 w-3.5" />
            </button>
            </div>
          </div>
        }
      />
      {/* Category-grouped view */}
      {viewMode === 'categories' && !search.trim() ? (
        /* Category-grouped browse — only when not searching. A search is a
           "find a product" action, so it bypasses the grouping and shows the
           flat, filtered result list below (the category view's lazy,
           per-group loading can't filter to a single matching product). */
        <LibraryCategoriesView
          categories={categories ?? []}
          isLoading={isLoading}
          filterParams={folderFilterParams}
        />
      ) : /* Grid view */
      viewMode === 'grid' ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-3">
          {isLoading || (isFetching && products.length === 0) ? (
            <p className="col-span-full py-12 text-center text-[12px] text-[var(--ink-3)]">Loading...</p>
          ) : products.length === 0 ? (
            <div className="col-span-full"><EmptyState icon={Package} title="No products found" description="Create your first product to get started." /></div>
          ) : products.map((product) => (
            /* A view renders the result set, it never narrows it. Filtering
               here ran after pagination, so the grid could disagree with both
               the count above it and the other views. */
            <ProductGridCard
              key={product.uuid}
              testId={`product-library-item-${product.uuid}`}
              title={product.cached_short_name}
              subtitle={product.category?.name}
              badge={
                product.product_type !== 'VARIANT_BASE' || !product.variant_count
                  ? undefined
                  : product.matching_variant_count == null
                    ? t('products.variantCount', { count: product.variant_count })
                    : t('products.variantCountMatching', {
                        matched: product.matching_variant_count,
                        total: product.variant_count,
                      })
              }
              imageUrl={product.image?.file_url}
              status={product.status}
              onClick={() => navigate(`/manage/products/${product.uuid}`)}
            />
          ))}
        </div>
      ) : (
      /* Table */
      (<DataTable<Product>
        tableId="products"
        columns={columns}
        data={products}
        totalCount={totalCount}
        isLoading={isLoading}
        isFetching={isFetching}
        isError={isError}
        onRetry={refetch}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        sorting={sorting}
        columnOrder={columnOrder}
        onSortingChange={setSorting}
        emptyStateNode={<EmptyState icon={Package} title="No products found" description="Create your first product to get started." />}
        columnVisibility={columnVisibility}
        renderRow={(product, visibleColumns) => {
          const isBase = product.product_type === 'VARIANT_BASE'
          const isExpanded = expandedProducts.has(product.uuid)
          return (
            <ProductRow
              key={product.uuid}
              product={product}
              isBase={isBase}
              isExpanded={isExpanded}
              onToggleExpand={() => toggleExpand(product.uuid)}
              visibleColumns={visibleColumns}
              attributeMeta={attributeMetaByCode}
              formatOptions={formatOptions}
              search={search.trim() || undefined}
            />
          )
        }}
      />)
      )}
      {/* New product dialog */}
      <NewProductDialog
        open={showNewProduct}
        onOpenChange={setShowNewProduct}
      />
    </>
  );
}

// ─── New Product Dialog ──────────────────────────────────

function NewProductDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const [step, setStep] = useState<'template' | 'form'>('template')
  const [selectedTemplateUuid, setSelectedTemplateUuid] = useState<string | null>(null)
  const [formValues, setFormValues] = useState<Record<string, unknown>>({})
  const [categoryUuid, setCategoryUuid] = useState<string | null>(null)
  const [imageUuid, setImageUuid] = useState<string | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  // Which action's requirements the markers reflect. Starts at 'save' so only
  // draft-required fields are marked; a publish attempt flips it to 'publish'
  // so publish-required fields gain their marker exactly when they're needed.
  const [attemptedMode, setAttemptedMode] = useState<SubmissionMode>('save')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const { data: templates } = useProductTemplates()
  const { data: categories } = useProductCategories()
  const { data: formFields, isLoading: fieldsLoading } = useProductFormMetadata(
    step === 'form' ? selectedTemplateUuid : null
  )
  const createProduct = useCreateProduct()

  const selectedTemplate = templates?.find((tmpl) => tmpl.uuid === selectedTemplateUuid)

  // Auto-advance when only one template exists
  useEffect(() => {
    if (open && templates?.length === 1 && step === 'template') {
      handleSelectTemplate(templates[0].uuid)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, templates])

  function handleClose() {
    onOpenChange(false)
    // Reset after animation
    setTimeout(() => {
      setStep('template')
      setSelectedTemplateUuid(null)
      setFormValues({})
      setCategoryUuid(null)
      setImageUuid(null)
      setImagePreview(null)
      setAttemptedMode('save')
      setFieldErrors({})
    }, 200)
  }

  function handleSelectTemplate(uuid: string) {
    setSelectedTemplateUuid(uuid)
    setFormValues({})
    setCategoryUuid(null)
    setImageUuid(null)
    setImagePreview(null)
    setAttemptedMode('save')
    setFieldErrors({})
    setStep('form')
  }

  function handleBack() {
    setStep('template')
  }

  function setFieldValue(codeName: string, value: unknown) {
    setFormValues((prev) => ({ ...prev, [codeName]: value }))
    // Clear a field's required error as soon as the user starts filling it.
    setFieldErrors((prev) => {
      if (!prev[codeName]) return prev
      const next = { ...prev }
      delete next[codeName]
      return next
    })
  }

  async function handleCreate(status: 'DRAFT' | 'PUBLISHED') {
    if (!selectedTemplateUuid) return

    const mode = status === 'PUBLISHED' ? 'publish' as const : 'save' as const
    // Reflect the requirements of the action being taken: publishing surfaces
    // the publish-required markers, saving a draft only the draft-required ones.
    setAttemptedMode(mode)
    if (sortedFormFields?.length) {
      const errors: Record<string, string> = {}
      for (const field of sortedFormFields) {
        const normalised = normaliseRegistrationField(field)
        if (isFieldRequired(normalised, mode)) {
          const val = formValues[field.code_name]
          if (
            val === undefined ||
            val === null ||
            val === '' ||
            (Array.isArray(val) && val.length === 0)
          ) {
            errors[field.code_name] = t('validation.fieldRequired', {
              field: field.display_name,
            })
          }
        }
      }
      setFieldErrors(errors)
      if (Object.keys(errors).length > 0) {
        // Keep the button enabled and point the user at the missing fields
        // instead of blocking silently.
        return
      }
    }

    try {
      await createProduct.mutateAsync({
        product_template_uuid: selectedTemplateUuid,
        attribute_values: formValues,
        category_uuid: categoryUuid,
        image_uuid: imageUuid,
        status,
      })
      toast.success(status === 'PUBLISHED' ? t('products.toasts.published') : t('products.toasts.savedDraft'))
      handleClose()
    } catch (err) {
      toastApiError(err, t('products.toasts.createFailed'))
    }
  }

  // Flatten categories for the select dropdown. `depth` drives the visual
  // indent; `path` is the full breadcrumb, which is what makes two leaves
  // that share a name (e.g. Hip & Ridge under both Roofing Components and
  // Shingle Lines) tellable apart once one is selected.
  function flattenCategories(
    cats: typeof categories,
    depth = 0,
    prefix = ''
  ): Array<{ uuid: string; name: string; depth: number; path: string }> {
    if (!cats) return []
    const result: Array<{
      uuid: string
      name: string
      depth: number
      path: string
    }> = []
    for (const cat of cats) {
      const path = prefix ? `${prefix} > ${cat.name}` : cat.name
      result.push({ uuid: cat.uuid, name: cat.name, depth, path })
      if (cat.children?.length) {
        result.push(...flattenCategories(cat.children, depth + 1, path))
      }
    }
    return result
  }

  // Indent by nesting level. A finite set of classes rather than a computed
  // style, and real padding rather than leading spaces - HTML collapses
  // repeated whitespace, so the old `'  '.repeat(depth)` indent never showed.
  const CATEGORY_INDENT = ['', 'pl-5', 'pl-9', 'pl-13'] as const

  // eslint-disable-next-line react-hooks/exhaustive-deps -- flattenCategories is a stable local function
  const flatCats = useMemo(() => flattenCategories(categories), [categories])

  // Sort form fields: name-format fields first, then the rest
  const sortedFormFields = useMemo(() => {
    if (!formFields?.length || !selectedTemplate) return formFields ?? []
    // Extract field names from the short_name_format, e.g. "{name}" → ["name"]
    const fmt = selectedTemplate.short_name_format || ''
    const nameFormatFields: string[] = []
    const regex = /\{(\w+)\}/g
    let match
    while ((match = regex.exec(fmt)) !== null) {
      nameFormatFields.push(match[1])
    }
    const nameSet = new Set(nameFormatFields)
    const priorityFields = formFields.filter((f) => nameSet.has(f.code_name))
    const remainingFields = formFields.filter((f) => !nameSet.has(f.code_name))
    // Sort priority fields in format order
    priorityFields.sort((a, b) => nameFormatFields.indexOf(a.code_name) - nameFormatFields.indexOf(b.code_name))
    return [...priorityFields, ...remainingFields]
  }, [formFields, selectedTemplate])

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          {step === 'form' && templates && templates.length > 1 && (
            <button
              onClick={handleBack}
              className="mb-1 flex cursor-pointer items-center gap-1 text-[11px] text-[var(--ink-3)]"
            >
              <ArrowLeft className="h-3 w-3" />
              {t('products.changeTemplate')}
            </button>
          )}
          <DialogTitle>
            {step === 'template' ? t('products.selectTemplate') : `${t('products.new')} ${selectedTemplate?.name ?? t('products.product')}`}
          </DialogTitle>
        </DialogHeader>

        {step === 'template' ? (
          /* ── Step 1: Template list ── */
          (<div>
            {!templates?.length ? (
              <p className="py-6 text-center text-[13px] text-[var(--ink-3)]">
                {t('products.noTemplatesAvailable')}
              </p>
            ) : (
              <ul className="-mx-6 max-h-[60vh] divide-y divide-[var(--rule)] overflow-y-auto border-y border-[var(--rule)]">
                {templates.map((tmpl) => (
                  <li key={tmpl.uuid}>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => handleSelectTemplate(tmpl.uuid)}
                      className="h-auto w-full flex-col items-start justify-start gap-0.5 rounded-none px-6 py-3 text-left font-normal hover:bg-[var(--bg-subtle)]"
                    >
                      <span className="block text-[13px] text-[var(--ink)]">
                        {tmpl.name}
                      </span>
                      {tmpl.attribute_count !== undefined && (
                        <span className="text-[11px] text-[var(--ink-3)]">
                          {t('products.attributeCount', { count: tmpl.attribute_count })}
                        </span>
                      )}
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>)
        ) : (
          /* ── Step 2: Metadata-driven form ── */
          (<div className="py-2">
            {fieldsLoading ? (
              <p className="py-4 text-center text-[13px] text-[var(--ink-3)]">
                {t('products.loadingForm')}
              </p>
            ) : !formFields?.length ? (
              <p className="py-4 text-center text-[13px] text-[var(--ink-3)]">
                {t('products.noProductAttributes')}
              </p>
            ) : (
              <div className="flex flex-col gap-4 max-h-[60vh] overflow-y-auto pr-1">
                {/* Name-format fields first */}
                {sortedFormFields
                  .filter((f) => {
                    const fmt = selectedTemplate?.short_name_format || ''
                    return fmt.includes(`{${f.code_name}}`)
                  })
                  .map((field) => (
                    <MetadataField
                      key={field.code_name}
                      field={normaliseRegistrationField(field)}
                      value={formValues[field.code_name]}
                      onChange={(v) => setFieldValue(field.code_name, v)}
                      submissionMode={attemptedMode}
                      error={fieldErrors[field.code_name]}
                    />
                  ))}

                {/* Category selector */}
                {flatCats.length > 0 && (
                  <div>
                    <Label className="clara-section-label mb-1.5 block">{t('products.category')}</Label>
                    <Select
                      value={categoryUuid ?? ''}
                      onValueChange={(v) => setCategoryUuid(v || null)}
                    >
                      <SelectTrigger size="sm" className="text-[13px] w-full">
                        <SelectValue placeholder={t('common.none')}>
                          {categoryUuid ? flatCats.find((c) => c.uuid === categoryUuid)?.path ?? t('common.none') : t('common.none')}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="">{t('common.none')}</SelectItem>
                        {flatCats.map((c) => (
                          <SelectItem
                            key={c.uuid}
                            value={c.uuid}
                            className={
                              CATEGORY_INDENT[
                                Math.min(c.depth, CATEGORY_INDENT.length - 1)
                              ]
                            }
                          >
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Product image */}
                <ImageUpload
                  value={imageUuid}
                  previewUrl={imagePreview}
                  onChange={(uuid, file) => {
                    setImageUuid(uuid)
                    setImagePreview(file?.file_url ?? null)
                  }}
                  previewClassName="h-[200px] object-cover p-0"
                />

                {/* Remaining attribute fields */}
                {sortedFormFields
                  .filter((f) => {
                    const fmt = selectedTemplate?.short_name_format || ''
                    return !fmt.includes(`{${f.code_name}}`)
                  })
                  .map((field) => (
                    <MetadataField
                      key={field.code_name}
                      field={normaliseRegistrationField(field)}
                      value={formValues[field.code_name]}
                      onChange={(v) => setFieldValue(field.code_name, v)}
                      submissionMode={attemptedMode}
                      error={fieldErrors[field.code_name]}
                    />
                  ))}
              </div>
            )}
          </div>)
        )}

        {step === 'form' && (
          <DialogFooter>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleCreate('DRAFT')}
                disabled={createProduct.isPending}
                className="px-4 py-1.5 rounded-[4px] border-[0.5px] border-[var(--rule-strong)] text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)] transition-all duration-150 cursor-pointer disabled:opacity-40 hover:bg-[var(--bg-subtle)]"
              >
                {t('products.saveDraft')}
              </button>
              <button
                data-testid="product-library-dialog-submit"
                onClick={() => handleCreate('PUBLISHED')}
                disabled={createProduct.isPending}
                className="px-4 py-1.5 rounded-[4px] text-[11px] uppercase tracking-[0.1em] transition-all duration-150 cursor-pointer disabled:opacity-40 bg-[var(--green)] hover:bg-[var(--green-hover)] text-[var(--bg)]"
              >
                {t('products.publish')}
              </button>
            </div>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ProductRow({
  product,
  isBase,
  isExpanded,
  onToggleExpand,
  visibleColumns,
  attributeMeta,
  formatOptions,
  search,
}: {
  product: Product
  isBase: boolean
  isExpanded: boolean
  onToggleExpand: () => void
  visibleColumns: DataTableColumn<Product>[]
  attributeMeta: Map<string, AttributeMeta>
  formatOptions: AttributeFormatOptions
  search?: string
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { formatDate } = useFormatters()
  const typeLabel =
    product.product_type === 'VARIANT_BASE'
      ? t('products.typeBase')
      : product.product_type === 'VARIANT'
        ? t('products.typeVariant')
        : t('products.typeProduct')

  function renderCell(col: DataTableColumn<Product>) {
    switch (col.id) {
      case 'name':
        return (
          <td key={col.id} className="px-4 py-3">
            <div className="flex items-center gap-3">
              {isBase && (
                <button
                  onClick={(e) => { e.stopPropagation(); onToggleExpand() }}
                  className="h-5 w-5 flex items-center justify-center rounded-[3px] cursor-pointer text-[var(--ink-3)] transition-all duration-100"
                >
                  {isExpanded ? (
                    <ChevronDown className="h-3.5 w-3.5" />
                  ) : (
                    <ChevronRight className="h-3.5 w-3.5" />
                  )}
                </button>
              )}
              <Image
                src={product.image?.file_url}
                thumbnailSrc={product.image?.thumbnail_url}
                alt=""
                className="h-8 w-8 shrink-0 rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)]"
                fallback={<Package className="h-3.5 w-3.5 text-[var(--ink-3)]" />}
              />
              <span className="text-[13px] text-[var(--ink)]">
                {product.cached_short_name || t('products.untitled')}
              </span>
            </div>
          </td>
        );
      case 'template':
        return (
          <td key={col.id} className="px-4 py-3 text-[12px] text-[var(--ink-3)]">
            {product.product_template?.name ?? ''}
          </td>
        );
      case 'category':
        return (
          <td key={col.id} className="px-4 py-3 text-[12px] text-[var(--ink-3)]">
            {product.category?.name ?? '-'}
          </td>
        );
      case 'status':
        return (
          <td key={col.id} className="px-4 py-3">
            <StatusBadge status={product.status} />
          </td>
        )
      case 'type':
        return (
          <td key={col.id} className="px-4 py-3 text-[12px] text-[var(--ink-3)]">
            {typeLabel}
          </td>
        );
      case 'variants':
        return (
          <td key={col.id} className="px-4 py-3 text-[12px] text-[var(--ink-3)]">
            {isBase ? product.variant_count : '-'}
          </td>
        );
      case 'registrations':
        return (
          <td key={col.id} className="px-4 py-3 text-[12px] text-[var(--ink-3)]">
            {product.registration_count ?? 0}
          </td>
        );
      case 'created_at':
        return (
          <td key={col.id} className="px-4 py-3 text-[11px] text-[var(--ink-3)]">
            {formatDate(product.created_at)}
          </td>
        );
      default: {
        // Dynamic attribute column - render from product_data (strip attr:
        // prefix) formatted via the shared helper so MONEY columns (e.g.
        // price_with_receipt / price_without_receipt) render as currency
        // rather than echoing the raw { amount, currency } dict.
        const attrKey = col.id.startsWith('attr:') ? col.id.slice(5) : col.id
        return (
          <td key={col.id} className="px-4 py-3 text-[12px] text-[var(--ink-3)]">
            {renderAttributeValue(attrKey, product.product_data?.[attrKey], attributeMeta.get(attrKey), formatOptions)}
          </td>
        );
      }
    }
  }

  return (
    <>
      <tr
        data-testid={`product-library-item-${product.uuid}`}
        className="cursor-pointer border-b-[0.5px] border-[var(--rule)] transition-all duration-100 hover:bg-[var(--bg-subtle)]"
        onClick={() => navigate(`/manage/products/${product.uuid}`)}
      >
        {visibleColumns.map((col) => renderCell(col))}
      </tr>
      {isBase && isExpanded && <VariantRows productUuid={product.uuid} parentName={product.cached_short_name} visibleColumns={visibleColumns} attributeMeta={attributeMeta} formatOptions={formatOptions} search={search} />}
    </>
  )
}
